'use client';

import {CheckCircle, XCircleIcon, Loader2, Mail} from "lucide-react";
import {Input} from "@/components/ui/input";
import {Button} from "@/components/ui/button";
import {useCallback, useState} from "react";
import useAnalytics from "@/hooks/useAnalytics";

class ApiSubscriptionError extends Error {};

const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { track } = useAnalytics();

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    track("subscribe_newsletter");
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/subscribe', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!data.success) {
          throw new ApiSubscriptionError(data.error);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (e) {
      if (e instanceof ApiSubscriptionError) {
        setError(e.message);
      } else {
        setError('There was an error subscribing to newsletter. Please try again later.');
      }
    }
    setIsSubmitting(false);
  }, [email]);

  return (
    <section className="py-16 px-6 max-w-2xl mx-auto text-center space-y-6">
      <div className="flex justify-center text-primary">
        <Mail className="w-8 h-8" />
      </div>
      <h2 className="text-2xl font-bold">See What’s Really Being Said.</h2>
      <p className="text-sm text-muted-foreground">
        Subscribe to our weekly recap of the most manipulative headlines, trending propaganda stories, and breakdowns of the techniques used to influence public opinion.
      </p>

      {success ? (
        <div className="flex items-center justify-center gap-2 text-green-600">
          <CheckCircle className="w-5 h-5" /> Subscribed successfully!
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 justify-center">
          <Input
            type="email"
            required
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full sm:w-72"
          />
          <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />} Subscribe for Free
          </Button>
        </form>
      )}
      {error && (
        <div className="flex items-center justify-center gap-2 text-destructive">
          <XCircleIcon className="w-5 h-5" /> {error}
        </div>
      )}
      <p className="text-xs text-muted-foreground">No spam. Unsubscribe anytime.</p>
    </section>
  )
}

export default NewsletterSection;
